import { useEffect, useState, useCallback } from "react"
import { supabase } from "@/lib/supabase"
import { useToast } from "@/context/ToastContext"
import { unsuspendUser } from "@/services/user.service"
import { Badge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { CheckCircle, RefreshCw } from "lucide-react"

interface SuspendedUser {
  id: string
  email: string
  full_name: string | null
  suspended_at: string | null
  suspension_reason: string | null
}

export function SuspendedPage() {
  const { toast } = useToast()
  const [users, setUsers]   = useState<SuspendedUser[]>([])
  const [loading, setLoading] = useState(true)
  const [acting, setActing]   = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from("users")
        .select("id, email, full_name, suspended_at, suspension_reason")
        .eq("is_suspended", true)
        .order("suspended_at", { ascending: false })
      if (error) throw error
      setUsers((data ?? []) as SuspendedUser[])
    } catch { toast("Failed to load suspended users", "error") }
    finally { setLoading(false) }
  }, [toast])

  useEffect(() => { load() }, [load])

  return (
    <div className="space-y-5">
      <div className="flex justify-between">
        <div />
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex justify-between">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Suspended Users</h2>
          <Badge variant="danger">{users.length}</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["User", "Suspended", "Reason", "Actions"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={4} /> :
               users.length === 0 ? (
                <tr><td colSpan={4}>
                  <EmptyState icon="✅" title="No suspended users" subtitle="All accounts are in good standing." />
                </td></tr>
               ) : users.map(u => (
                <tr key={u.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-bold text-sm text-gray-900">{u.full_name ?? "—"}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {u.suspended_at ? new Date(u.suspended_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="danger">{u.suspension_reason ?? "Not specified"}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Button size="sm" variant="success" icon={<CheckCircle size={13} />}
                      loading={acting === u.id}
                      onClick={async () => {
                        setActing(u.id)
                        try { await unsuspendUser(u.id); toast("User unsuspended", "success"); load() }
                        catch { toast("Failed to unsuspend", "error") }
                        finally { setActing(null) }
                      }}>
                      Unsuspend
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}